import { FilmStrip } from '@phosphor-icons/react';
import { useListMedia } from '@reelplicate/api-client/media';
import type { MediaAssetDto } from '@reelplicate/api-client/model';
import {
  Button,
  EmptyState,
  FilterChip,
  Inline,
  Modal,
  ModalClose,
  ModalContent,
  SearchField,
  Skeleton,
} from '@reelplicate/ui';
import { useMemo, useState } from 'react';

import { formatDuration } from '@/features/media/library';
import { toAsset } from '@/features/media/media-api';
import { MediaTile } from '@/features/media/media-tile';
import type { Owner } from '@/features/media/types';

import { fits, MAX_SLIDES, type PostType } from './form';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'generated', label: 'Generated' },
  { id: 'uploaded', label: 'Uploaded' },
  { id: 'unposted', label: 'Not posted yet' },
] as const;
type Filter = (typeof FILTERS)[number]['id'];

const matches = (asset: MediaAssetDto, filter: Filter, query: string) => {
  if (filter === 'generated' && asset.source !== 'GENERATED') return false;
  if (filter === 'uploaded' && asset.source !== 'UPLOADED') return false;
  if (
    filter === 'unposted' &&
    (asset.status === 'SCHEDULED' || asset.status === 'PUBLISHED')
  )
    return false;
  if (!query) return true;
  const q = query.toLowerCase();
  return [asset.title, asset.caption, asset.transcript, ...asset.tags].some((text) =>
    text?.toLowerCase().includes(q),
  );
};

/**
 * Figma "Modal · Choose from Media" (358:3685): the library's reels that
 * fit a post (9:16 video under 3 minutes), or its images for a carousel
 * (up to 10, in the order picked), searchable and filterable.
 */
export function ChooseMediaModal({
  open,
  onOpenChange,
  workspaceId,
  type,
  owners,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  type: PostType;
  owners: Owner[];
  onAdd: (assets: MediaAssetDto[]) => void;
}) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      {open && (
        <Picker
          workspaceId={workspaceId}
          type={type}
          owners={owners}
          onAdd={(assets) => {
            onAdd(assets);
            onOpenChange(false);
          }}
        />
      )}
    </Modal>
  );
}

function Picker({
  workspaceId,
  type,
  owners,
  onAdd,
}: {
  workspaceId: string;
  type: PostType;
  owners: Owner[];
  onAdd: (assets: MediaAssetDto[]) => void;
}) {
  const { data: library } = useListMedia(workspaceId, {
    query: { enabled: Boolean(workspaceId) },
  });
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string[]>([]);

  const usable = useMemo(
    () => (library?.items ?? []).filter((asset) => asset.fileUrl && fits(asset, type)),
    [library, type],
  );
  const shown = usable.filter((asset) => matches(asset, filter, query.trim()));
  const byId = new Map(usable.map((asset) => [asset.id, asset]));
  const multiple = type === 'carousel';

  const toggle = (id: string, on: boolean) =>
    setPicked((prev) => {
      if (!on) return prev.filter((p) => p !== id);
      if (!multiple) return [id];
      return prev.length >= MAX_SLIDES ? prev : [...prev, id];
    });

  const first = byId.get(picked[0] ?? '');
  const summary =
    picked.length === 0
      ? multiple
        ? `Pick up to ${MAX_SLIDES}, in the order they should appear.`
        : 'Pick a reel.'
      : picked.length === 1 && first
        ? `1 selected · ${first.title}${first.durationSeconds ? `, ${formatDuration(first.durationSeconds)}` : ''}`
        : `${picked.length} selected`;

  return (
    <ModalContent
      title="Choose from Media"
      description={
        multiple
          ? 'Images from your library, for the carousel’s slides.'
          : 'Reels from your library. Only 9:16 videos under 3 minutes are shown.'
      }
      className="h-[min(760px,calc(100vh-64px))] w-230 [&_[data-slot=modal-footer]]:bg-bg-surface"
      footerStart={
        <span className="text-body-sm text-text-secondary truncate">{summary}</span>
      }
      footer={
        <>
          <ModalClose asChild>
            <Button variant="outline">Cancel</Button>
          </ModalClose>
          <Button
            disabled={picked.length === 0}
            onClick={() => onAdd(picked.flatMap((id) => byId.get(id) ?? []))}
          >
            Add to post
          </Button>
        </>
      }
    >
      <Inline gap={2} wrap className="bg-bg-surface sticky top-0 z-10 pb-4">
        <SearchField
          aria-label="Search media"
          placeholder="Search by title, caption or what’s in the video"
          value={query}
          onValueChange={setQuery}
          shortcut={false}
          className="min-w-60 flex-1"
        />
        {FILTERS.map(({ id, label }) => (
          <FilterChip
            key={id}
            selected={filter === id}
            onSelectedChange={() => setFilter(id)}
          >
            {label}
          </FilterChip>
        ))}
      </Inline>
      {!library ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} shape="rectangle" shimmer className="aspect-[197/350]" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <EmptyState
          icon={FilmStrip}
          title={usable.length === 0 ? 'Nothing that fits yet' : 'No matches'}
          description={
            usable.length === 0
              ? multiple
                ? 'Add images to your library, or upload them here.'
                : 'Add a 9:16 video under 3 minutes to your library, or upload one here.'
              : 'Try another search or filter.'
          }
        />
      ) : (
        <ul
          aria-label="Media"
          className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-4"
        >
          {shown.map((asset) => {
            const selected = picked.includes(asset.id);
            return (
              <li key={asset.id}>
                <MediaTile
                  asset={toAsset(asset, owners)}
                  selected={selected}
                  selecting={picked.length > 0}
                  onSelectedChange={(on) => toggle(asset.id, on)}
                  onOpen={() => toggle(asset.id, !selected)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </ModalContent>
  );
}
